import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import FolderSpecialRoundedIcon from "@mui/icons-material/FolderSpecialRounded";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";
import DashboardLoadingState from "../../../components/dashboard/DashboardLoadingState";
import OwnerPersonDrawer from "./center/OwnerPersonDrawer";
import OwnerMovementImportDialog from "./center/OwnerMovementImportDialog";
import OwnerRecurringFinanceDialog from "./center/OwnerRecurringFinanceDialog";
import OwnerRecurringFinancePanel from "./center/OwnerRecurringFinancePanel";
import OwnerRecurringRecordDialog from "./center/OwnerRecurringRecordDialog";
import OwnerRecurringPeriodsDialog from "./center/OwnerRecurringPeriodsDialog";
import OwnerRecurringFromTransactionDialog from "./center/OwnerRecurringFromTransactionDialog";
import OwnerProposalQuickDialog from "./center/OwnerProposalQuickDialog";
import OwnerChatGptExportDialog from "./center/OwnerChatGptExportDialog";
import OwnerDailyPlan from "./center/OwnerDailyPlan";
import OwnerMoneyRealityStrip from "./center/OwnerMoneyRealityStrip";
import OwnerJarvis2 from "./center/OwnerJarvis2";
import OwnerHourBoard from "./center/OwnerHourBoard";
import OwnerCommitmentTimeDialog from "./center/OwnerCommitmentTimeDialog";
import OwnerWorkLogDialog from "./center/OwnerWorkLogDialog";
import {
  OwnerAccountDialog,
  OwnerClientDialog,
  OwnerCollectDialog,
  OwnerCommitmentDialog,
  OwnerFinanceDialog,
  OwnerInitiativeDialog,
  OwnerInteractionDialog,
} from "./center/OwnerQuickDialogs";
import { OwnerRoutineDialog } from "./calendar/OwnerCalendarDialogs";

function localYmd(value = new Date()) {
  const pad = (number) => String(number).padStart(2, "0");
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}

export default function OwnerPanelPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [center, setCenter] = React.useState(null);
  const [assistant, setAssistant] = React.useState(null);
  const [dailyCalendar, setDailyCalendar] = React.useState(null);
  const [assistantCurrency, setAssistantCurrency] = React.useState("ARS");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [dialog, setDialog] = React.useState("");
  const [financeInitialType, setFinanceInitialType] = React.useState("expense");
  const [sourceEntry, setSourceEntry] = React.useState(null);
  const [saleToCollect, setSaleToCollect] = React.useState(null);
  const [relationPerson, setRelationPerson] = React.useState(null);
  const [recurringToRecord, setRecurringToRecord] = React.useState(null);
  const [recurringOccurrenceToRecord, setRecurringOccurrenceToRecord] = React.useState(null);
  const [recurringPeriodsPlan, setRecurringPeriodsPlan] = React.useState(null);
  const [recurringToEdit, setRecurringToEdit] = React.useState(null);
  const [transactionToRecurring, setTransactionToRecurring] = React.useState(null);
  const [selectedPerson, setSelectedPerson] = React.useState(null);
  const [personContext, setPersonContext] = React.useState(null);
  const [personLoading, setPersonLoading] = React.useState(false);
  const [personError, setPersonError] = React.useState("");
  const [chatGptPerson, setChatGptPerson] = React.useState(null);
  const [initialScheduledAt, setInitialScheduledAt] = React.useState("");
  const [timeCommitment, setTimeCommitment] = React.useState(null);
  const [workLogAt, setWorkLogAt] = React.useState("");

  const load = React.useCallback(async () => {
    try {
      setError("");
      const [centerResponse, assistantResponse, calendarResponse] = await Promise.all([
        ownerApi.center(),
        ownerApi.assistant({ currency: assistantCurrency }),
        ownerApi.calendar({ date: localYmd() }),
      ]);
      setCenter(centerResponse.center);
      setAssistant(assistantResponse.assistant);
      setDailyCalendar(calendarResponse.calendar);
    } catch (err) {
      setError(formatApiError(err, "No pude cargar el Panel rápido."));
    } finally {
      setLoading(false);
    }
  }, [assistantCurrency]);

  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    const action = searchParams.get("jarvisAction");
    if (!action) return;
    if (action === "finance") { setFinanceInitialType("expense"); setDialog("finance"); }
    if (action === "consumption") { setFinanceInitialType("consumption"); setDialog("finance"); }
    if (["client", "proposal", "commitment", "routine", "recurring", "interaction", "initiative", "import"].includes(action)) setDialog(action);
    if (action === "worklog") setWorkLogAt(new Date().toISOString());
    const next = new URLSearchParams(searchParams);
    next.delete("jarvisAction");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const loadPersonContext = React.useCallback(async (person, { reset = true } = {}) => {
    const personId = person?.id || person?._id;
    if (!personId) return;
    if (reset) setPersonContext(null);
    setPersonLoading(true);
    setPersonError("");
    try {
      const response = await ownerApi.personContext(personId);
      setPersonContext(response.context);
    } catch (err) {
      setPersonError(formatApiError(err, "No pude cargar la ficha de esta persona."));
    } finally {
      setPersonLoading(false);
    }
  }, []);

  const closeDialogs = () => {
    setDialog("");
    setSourceEntry(null);
    setSaleToCollect(null);
    setRelationPerson(null);
    setRecurringToRecord(null);
    setRecurringOccurrenceToRecord(null);
    setRecurringPeriodsPlan(null);
    setRecurringToEdit(null);
    setTransactionToRecurring(null);
    setInitialScheduledAt("");
  };

  const refreshAfterAction = async () => {
    await load();
    if (selectedPerson) await loadPersonContext(selectedPerson, { reset: false });
  };

  if (loading && !center) return <DashboardLoadingState label="Preparando tu Panel rápido…" minHeight={420} />;

  const currency = assistantCurrency || assistant?.currency || "ARS";

  return (
    <Box sx={{ pb: { xs: 1.5, md: 1 }, maxWidth: 1660, mx: "auto", px: 0 }}>
  
        <Stack direction="row" spacing={.45} justifyContent="flex-end" sx={{ mb: .65 }}>
          {[
            ["Movimiento", <AccountBalanceWalletRoundedIcon />, () => setDialog("finance")],
            ["Recurrente", <RepeatRoundedIcon />, () => setDialog("recurring")],
            ["Persona", <PersonAddAltRoundedIcon />, () => setDialog("client")],
            ["Proyecto", <FolderSpecialRoundedIcon />, () => setDialog("initiative")],
            ["Importar", <UploadFileRoundedIcon />, () => setDialog("import")],
          ].map(([label, icon, onClick]) => (
            <Tooltip key={label} title={label}>
              <IconButton onClick={onClick} aria-label={label} sx={{ width: 40, height: 40, border: "1px solid", borderColor: "divider", borderRadius: 2.2 }}>{icon}</IconButton>
            </Tooltip>
          ))}
        </Stack>

        {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0,1.35fr) minmax(290px,.65fr)" }, gap: 1, mt: 1, alignItems: "stretch" }}>
          <OwnerJarvis2
            currency={currency}
            onChanged={refreshAfterAction}
          />

          <OwnerDailyPlan
            calendar={dailyCalendar}
            allCommitments={center?.life?.commitments || []}
            onOpenCalendar={() => navigate("/dashboard/owner/calendario")}
            onChanged={refreshAfterAction}
          />
        </Box>

        <Box sx={{ mt: 1 }}>
          <OwnerMoneyRealityStrip
            center={center}
            assistant={assistant}
            currency={currency}
            onCurrencyChange={setAssistantCurrency}
            onOpenFinance={() => navigate("/dashboard/owner/finanzas")}
          />
        </Box>

        <Box component="details" sx={{ mt: 1, border: "1px solid", borderColor: "divider", borderRadius: 2.5, bgcolor: "background.paper", overflow: "hidden", "&[open] > summary": { borderBottom: "1px solid", borderColor: "divider" } }}>
          <Box component="summary" sx={{ cursor: "pointer", listStyle: "none", px: 1.5, py: 1.1, fontWeight: 1000, userSelect: "none", "&::-webkit-details-marker": { display: "none" } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center"><Typography sx={{ fontWeight: 1000 }}>Más detalle</Typography><Typography sx={{ color: "text.secondary", fontSize: 12, fontWeight: 800 }}>horas · recurrentes</Typography></Stack>
          </Box>
          <Box sx={{ p: 1 }}>
            <OwnerHourBoard
              calendar={dailyCalendar}
              onLogTime={(commitment) => setTimeCommitment(commitment)}
              onLogAt={(value) => setWorkLogAt(value)}
            />
            <Box sx={{ mt: 1 }}>
              <OwnerRecurringFinancePanel
                recurring={center?.finance?.recurring}
                onCreate={() => { setRecurringToEdit(null); setRelationPerson(null); setDialog("recurring"); }}
                onRecord={(plan, occurrence) => { setRecurringToRecord(plan); setRecurringOccurrenceToRecord(occurrence || plan.nextOccurrence || null); setDialog("recurringRecord"); }}
                onPeriods={(plan) => { setRecurringPeriodsPlan(plan); setDialog("recurringPeriods"); }}
                onEdit={(plan) => { setRecurringToEdit(plan); setRelationPerson(null); setDialog("recurring"); }}
                onChanged={refreshAfterAction}
              />
            </Box>
          </Box>
        </Box>

        <OwnerPersonDrawer
          open={Boolean(selectedPerson)}
          person={selectedPerson}
          context={personContext}
          loading={personLoading}
          error={personError}
          onClose={() => { setSelectedPerson(null); setPersonContext(null); setPersonError(""); }}
          onInteraction={(person) => { setRelationPerson(person); setDialog("interaction"); }}
          onFollowUp={(person) => { setRelationPerson(person); setDialog("commitment"); }}
          onExportChatGpt={(person) => setChatGptPerson(person)}
          onMonthly={(person) => { setRelationPerson(person); setDialog("recurring"); }}
          onProposal={(person) => { setRelationPerson(person); setDialog("proposal"); }}
          onProject={(person) => { setRelationPerson(person); setDialog("initiative"); }}
        />

        <OwnerChatGptExportDialog open={Boolean(chatGptPerson)} person={chatGptPerson} onClose={() => setChatGptPerson(null)} />
        <OwnerProposalQuickDialog open={dialog === "proposal"} onClose={closeDialogs} onSaved={refreshAfterAction} preferredClient={relationPerson} />
        <OwnerAccountDialog open={dialog === "account"} onClose={closeDialogs} onSaved={refreshAfterAction} />
        <OwnerFinanceDialog open={dialog === "finance"} onClose={closeDialogs} accounts={center?.finance?.accounts || []} initialType={financeInitialType} onSaved={refreshAfterAction} />
        <OwnerClientDialog open={dialog === "client"} onClose={closeDialogs} onSaved={refreshAfterAction} />
        <OwnerRoutineDialog open={dialog === "routine"} onClose={closeDialogs} selectedDate={dailyCalendar?.selectedDate || localYmd()} onSaved={refreshAfterAction} />
        <OwnerCommitmentDialog open={dialog === "commitment" || dialog === "followup"} onClose={closeDialogs} onSaved={refreshAfterAction} sourceEntry={sourceEntry} initiatives={center?.life?.initiatives || []} person={relationPerson} initialScheduledAt={initialScheduledAt} initialKind={dialog === "followup" ? "follow_up" : ""} />
        <OwnerCommitmentTimeDialog open={Boolean(timeCommitment)} commitment={timeCommitment} onClose={() => setTimeCommitment(null)} onSaved={async () => { setTimeCommitment(null); await refreshAfterAction(); }} />
        <OwnerWorkLogDialog open={Boolean(workLogAt)} occurredAt={workLogAt} onClose={() => setWorkLogAt("")} onSaved={refreshAfterAction} />
        <OwnerInteractionDialog open={dialog === "interaction"} onClose={closeDialogs} onSaved={refreshAfterAction} person={relationPerson} />
        <OwnerInitiativeDialog open={dialog === "initiative"} onClose={closeDialogs} onSaved={refreshAfterAction} people={assistant?.clients || center?.people?.focus || []} preferredPerson={relationPerson} />
        <OwnerCollectDialog open={dialog === "collect"} onClose={closeDialogs} sale={saleToCollect} accounts={center?.finance?.accounts || []} onSaved={refreshAfterAction} />
        <OwnerMovementImportDialog open={dialog === "import"} onClose={closeDialogs} onCommitted={refreshAfterAction} />
        <OwnerRecurringFinanceDialog open={dialog === "recurring"} onClose={closeDialogs} accounts={center?.finance?.accounts || []} onSaved={refreshAfterAction} preferredClient={relationPerson} plan={recurringToEdit} />
        <OwnerRecurringRecordDialog open={dialog === "recurringRecord"} onClose={closeDialogs} plan={recurringToRecord} occurrence={recurringOccurrenceToRecord} accounts={center?.finance?.accounts || []} onSaved={refreshAfterAction} />
        <OwnerRecurringPeriodsDialog open={dialog === "recurringPeriods"} plan={recurringPeriodsPlan} onClose={closeDialogs} onRecord={(occurrence) => { setRecurringToRecord(recurringPeriodsPlan); setRecurringOccurrenceToRecord(occurrence); setDialog("recurringRecord"); }} />
        <OwnerRecurringFromTransactionDialog open={dialog === "recurringFromTransaction"} onClose={closeDialogs} transaction={transactionToRecurring} recurring={center?.finance?.recurring} onSaved={refreshAfterAction} />
    </Box>
  );
}
